import axios from 'axios';
import { api } from './api.ts';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';

const BASE = '/api/v1';

// ── Types ──────────────────────────────────────────────────────────────────

export type TransactionItemStatus = 'confirmed' | 'warning';

export interface ItemizationRule {
  id: number;
  user_id: number;
  name: string;
  pattern: string;
  category_id: number;
  category?: { id: number; name: string; icon: string; icon_color: string };
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRuleRequest {
  name: string;
  pattern: string;
  category_id: number;
  priority?: number;
}

export interface ItemizationPreviewResult {
  description: string;
  matched_rule_id: number | null;
  matched_rule_name: string;
}

export interface TransactionItem {
  id: number;
  transaction_id: number;
  user_id: number;
  description: string;
  amount: number;
  category_id: number | null;
  category?: { id: number; name: string; icon: string; icon_color: string } | null;
  status: TransactionItemStatus;
  matched_rule_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateItemRequest {
  description: string;
  amount: number;
  category_id?: number | null;
}

export interface ParseAndMatchResult {
  confirmed: number;
  warnings: number;
  items: TransactionItem[];
}

// ── Itemization Rules API ──────────────────────────────────────────────────

export const getRules = () =>
  api.get<ItemizationRule[]>(`${BASE}/itemization/rules`).then(r => r.data);

export const createRule = (data: CreateRuleRequest) =>
  api.post<ItemizationRule>(`${BASE}/itemization/rules`, data).then(r => r.data);

export const updateRule = (id: number, data: CreateRuleRequest) =>
  api.put<ItemizationRule>(`${BASE}/itemization/rules/${id}`, data).then(r => r.data);

export const deleteRule = (id: number) =>
  api.delete(`${BASE}/itemization/rules/${id}`).then(r => r.data);

export const reorderRules = (orderedIds: number[]) =>
  api.put(`${BASE}/itemization/rules/reorder`, { ordered_ids: orderedIds }).then(r => r.data);

export const previewRules = (descriptions: string[]) =>
  api.post<ItemizationPreviewResult[]>(`${BASE}/itemization/rules/preview`, { descriptions }).then(r => r.data);

// ── Converters API ────────────────────────────────────────────────────────

export const getConverters = () =>
  api.get<string[]>(`${BASE}/itemization/converters`).then(r => r.data);

// ── Warnings API ──────────────────────────────────────────────────────────

export const getWarnings = () =>
  api.get<TransactionItem[]>(`${BASE}/itemization/warnings`).then(r => r.data);

// ── Transaction Items API ─────────────────────────────────────────────────

export const getItems = (transactionId: number) =>
  api.get<TransactionItem[]>(`${BASE}/transactions/${transactionId}/items`).then(r => r.data);

export const createItem = (transactionId: number, data: CreateItemRequest) =>
  api.post<TransactionItem>(`${BASE}/transactions/${transactionId}/items`, data).then(r => r.data);

export const updateItem = (transactionId: number, itemId: number, data: CreateItemRequest) =>
  api.put<TransactionItem>(`${BASE}/transactions/${transactionId}/items/${itemId}`, data).then(r => r.data);

export const deleteItem = (transactionId: number, itemId: number) =>
  api.delete(`${BASE}/transactions/${transactionId}/items/${itemId}`).then(r => r.data);

export const importItems = (transactionId: number, formData: FormData) => {
  const token = localStorage.getItem('token');
  return axios.post<ParseAndMatchResult>(
    `${getApiBaseUrl()}${BASE}/transactions/${transactionId}/items/import`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  ).then(r => r.data);
};

export const downloadCSVTemplate = () => {
  const csv = 'date,description,amount\n2026-01-15,NETFLIX BRASIL,55.90\n2026-01-16,POSTO SHELL,200.00\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'template_itemizacao.csv';
  a.click();
  URL.revokeObjectURL(url);
};

export const CONVERTER_LABELS: Record<string, string> = {
  standard_csv: 'CSV Padrão (date, description, amount)',
  nubank_csv: 'Nubank CSV (date, title, amount)',
  generic_csv: 'CSV Genérico (auto-detectado)',
  generic_xlsx: 'XLSX Genérico',
  pdf: 'PDF (em desenvolvimento)',
};
